import EditIcon from "@/assets/icons/EditIcon";
import { Button } from "@/components/ui";
import { Heading, Paragraph } from "@/components/ui/Typography";
import { useState } from "react";
import { useMicroSiteContext } from "@/contexts/MicroSiteContext";
import Input from '@/components/ui/Input/Input';
import TextArea from '@/components/ui/TextArea/TextArea';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FileUpload from '@/components/ui/FileUpload';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import UploadIcon from "@/assets/icons/UploadIcon";
import { showToast } from '@/lib/toast';
import { TemplateDataSchemaType } from "@/pages/articles-pages/programmes/MultiStepForm/MicroSite/Templates/templateDataSchema";
import FullPageSpinner from "@/components/ui/FullPageSpinner";
import { sanitizeHtml } from "@/utils/helpers/html";

const FormDataSchema = z.object({
  headerTitle: z.string().min(1, 'Title is required'),
  headerDescription: z.string(),
  headerImageUrl: z.string(),
});

const Hero = () => {

  const { templatePayload:data, updateTemplatePayload } = useMicroSiteContext();
    const fileUploadMutation = useFileUpload();
    const isEdit = new URLSearchParams(window.location.search).get('action') === 'edit';
  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const {
      control,
      handleSubmit,
      setValue,
      formState: { errors },
      reset
    } = useForm({
      resolver: zodResolver(FormDataSchema),
      defaultValues: {
        headerTitle: data.headerSection.headerTitle ?? '',
        headerDescription: data.headerSection.headerDescription ?? '',
        headerImageUrl: data.headerSection.headerImageUrl ?? '',
      },
      mode: 'all'
    });

  const handleSave = (formData: TemplateDataSchemaType["headerSection"]) => {
    setIsEditing(false);
    const updatedPayload = {
      ...data,
      headerSection: {
        headerTitle: formData.headerTitle,
        headerDescription: formData.headerDescription,
        headerImageUrl: formData.headerImageUrl,
      },
    };
    updateTemplatePayload(updatedPayload);
  };

  

    const handleFileChange = async(files: File[] | null) => {
    if (files && files.length > 0) {
      setIsUploading(true);
      const file = files[0];
      try {
        const response = await fileUploadMutation.mutateAsync({
          file: file
        });
        setValue('headerImageUrl', response?.data?.fileUrl, { shouldValidate: true });
        setPreviewUrl(response?.data?.fileUrl);
      } catch (error) {
        console.error('File upload failed:', error);
        showToast.error('File upload failed. Please try again.');
      } finally {
        setIsUploading(false);
      }
    } else {
      setPreviewUrl(null);
      setValue('headerImageUrl', "", { shouldValidate: true });
    }
  };

  const PreviewComponent = () => {
    return (
      <div className=" relative flex flex-col  w-full min-h-96 xl:min-h-[35rem]">

        <div className="flex p-1 items-center justify-center">
            <img src={data.branding.websiteLogoUrl} alt="" className="h-12 object-contain w-fit min-w-fit" />
        </div>
        <div className="h-full flex items-center justify-center relative overflow-hidden flex-1">
          {/* Overlay */}
      <div className="absolute top-0 left-0 w-full h-full bg-black opacity-50 z-10"></div>
      {/* Bg Image */}
        <img src={previewUrl ?? data.headerSection.headerImageUrl ?? "/blank-image-placeholder.png"} alt={"Hero Section"} className="w-full object-cover h-full object-center absolute inset-0 " />

        {/* Title and description */}
        <div className="absolute z-20 items-center justify-center text-center flex flex-col gap-4 w-full px-4 lg:px-16">
        
          <div className="flex items-center gap-3">
          <Heading level={1} className="" style={{color: data.branding.textColorCode}}>{data.headerSection.headerTitle}</Heading>
{isEdit && <EditIcon fill="#fff" className="cursor-pointer" onClick={() => setIsEditing(true)} />}
          </div>
          <Paragraph  className=" lg:w-1/3" style={{color: data.branding.textColorCode}} dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.headerSection.headerDescription ?? "") }} ></Paragraph>
        </div>
        </div>
           
      </div>
    )
  };

  const FormComponent = () => {
    return (
      <div className="flex flex-col gap-4 w-full p-4 lg:p-16 bg-[#F2F2F2]">
        <form onSubmit={handleSubmit(handleSave)} className="flex flex-col gap-4">
          <div className="max-h-96 overflow-hidden">
            {isUploading ? <FullPageSpinner /> : <img src={previewUrl ?? data.headerSection.headerImageUrl ?? "/blank-image-placeholder.png"} alt="preview" className=" w-full h-full max-h-96 object-contain object-center" />}
          </div>
          <Controller
          name="headerTitle"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Title"
              label="Title"
              error={errors.headerTitle?.message}
              $dataId="headerTitle-input"
              required
            />
          )}
        />

           <Controller
          name="headerDescription"
          control={control}
          render={({ field }) => (
            <TextArea
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Description"
              label="Description"
              error={errors.headerDescription?.message}
              $dataId="headerDescription-input"
              textAreaClassName="min-h-[10rem] text-text-primary whitespace-pre-wrap"
              required
            />
          )}
        />

        <Controller
          name="headerImageUrl"
          control={control}
          render={({ field }) => (
            <FileUpload
              onChange={(files) => {
                handleFileChange(files);
                field.onChange(files ? URL.createObjectURL(files[0]) : '');
              }}
              label=""
              placeholder=""
              $maxSizeMB={5}
              error={errors.headerImageUrl?.message}
              multiple={false}
              allowedFileTypes={['image/*']}
              containerClassName="border border-[#E5E5E5] bg-white"
              $dataId="inline-file-upload"
            >
              <div className="flex flex-col items-center justify-center gap-4">
                <UploadIcon stroke="#00764B" />
                <Paragraph className="font-semibold text-primary">
                  Click or drag and drop file to upload
                </Paragraph>
                <Paragraph className="text-sm text-text-secondary">
                  Supported document file is PNG, & JPEG with max size of 5mb
                </Paragraph>
                <button
                  type="button"
                  className="bg-[#ECFEF8] text-[#00764B] hover:bg-[#ECFEF8] hover:text-[#000] px-4 py-2 rounded-md font-semibold"
                >
                  Browse file
                </button>
              </div>
            </FileUpload>
          )}
        />

          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="secondary"
              className=""
              onClick={() => {
                setIsEditing(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button
              className=""
              onClick={handleSubmit(handleSave)}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    )
  };


  return (
    <div>
      {isEditing ? <FormComponent /> : <PreviewComponent />}
    </div>
  )
}

export default Hero